import React, { useState, useEffect, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';
import { RadioButton } from 'primereact/radiobutton';
import { toast } from 'sonner';
import { useContent } from '../contexts/ContentContext';
import QuantityInput from './QuantityInput';
import CroppedImage from './CroppedImage';
import axios from 'axios';
import { generateCroppedThumbnail } from '../utils/imageCropper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ImageEditorModal({ data, printSizes, cartItems, onClose, onSave, allImages = [], setActiveTab, onDeleteImage, onUpdateThumbnail }) {
  const { t } = useContent();
  const [bulkDiscounts, setBulkDiscounts] = React.useState({});
  
  // Fetch bulk discounts
  React.useEffect(() => {
    const fetchBulkDiscounts = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/bulk-discounts`);
        const discountMap = {};
        response.data.discounts.forEach(d => {
          discountMap[d.product_sku] = {
            discount_price: d.discount_price,
            discount_quantity: d.discount_quantity
          };
        });
        setBulkDiscounts(discountMap);
        console.log('📊 Bulk discounts loaded:', discountMap);
      } catch (error) {
        console.error('Failed to load bulk discounts:', error);
      }
    };
    fetchBulkDiscounts();
  }, []);
  
  const isCartEdit = data.isCartEdit;
  
  // State to track which image we're currently viewing/editing
  const [currentImageIndex, setCurrentImageIndex] = useState(() => {
    if (isCartEdit) return 0; // Cart edits don't navigate between images
    const initialImage = data.image;
    const index = allImages.findIndex(img => img.id === initialImage.id);
    return index >= 0 ? index : 0;
  });
  
  // Get current image based on index
  // For cart edits, use the stable image_id (not the cart item's temporary id)
  const image = isCartEdit ? { 
    ...data.cartItem,
    id: data.cartItem.image_id || data.cartItem.filename, // Use image_id (stable) not cart item id (temporary)
    filename: data.cartItem.filename,
    thumbnail: data.cartItem.thumbnail,
    image_data: data.cartItem.image_data
  } : (allImages[currentImageIndex] || data.image);
  
  // Check if this image already exists in cart (by filename)
  const existingCartItem = !isCartEdit ? cartItems.find(item => item.filename === image.filename) : null;
  const isUpdatingExisting = !!existingCartItem;
  
  const initialSettings = isCartEdit ? {
    size: data.cartItem.sku || data.cartItem.size,  // Use SKU as the key
    paperType: data.cartItem.paper_type || 'matte',
    border: data.cartItem.border || false,
    quantity: data.cartItem.quantity || 1,
    // rotation removed
    scale: data.cartItem.scale || 100,
    cropSettings: data.cartItem.crop_settings || { x: 0, y: 0, width: 100, height: 100 },
    aspectOrientation: data.cartItem.aspectOrientation || 'landscape'  // Restore saved orientation
  } : (data.currentSettings || {
    size: Object.keys(printSizes)[0] || null,  // Use first available product SKU
    paperType: 'matte',
    border: false,
    quantity: 1,
    // rotation removed
    scale: 100,
    cropSettings: { x: 0, y: 0, width: 100, height: 100 }
  });

  // Debug: Log cart item data for cart edits
  if (isCartEdit) {
    console.log(`🛒 Cart Edit - cartItem:`, {
      sku: data.cartItem.sku,
      size: data.cartItem.size,
      aspectOrientation: data.cartItem.aspectOrientation,
      zoom: data.cartItem.zoom,
      cropPosition: data.cartItem.cropPosition
    });
  }

  // Initialize quantities for all sizes
  const initQuantities = {};
  const originalCartQuantities = {};
  
  Object.keys(printSizes).forEach(sizeKey => {
    if (isCartEdit) {
      // For cart edits, use the cartQuantities passed from CartSection (all sizes for this image)
      initQuantities[sizeKey] = data.cartQuantities?.[sizeKey] || 0;
      originalCartQuantities[sizeKey] = initQuantities[sizeKey];
    } else {
      const currentCartQty = cartItems
        ? cartItems
            .filter(item => 
              item.filename === image.filename && 
              (item.sku === sizeKey || item.size === sizeKey)  // Match by SKU first, fall back to size
              // Note: paper_type and border will be selected later in Order Summary
            )
            .reduce((sum, item) => sum + item.quantity, 0)
        : 0;
      
      initQuantities[sizeKey] = currentCartQty;
      originalCartQuantities[sizeKey] = currentCartQty;
    }
  });

  const [sizeQuantities, setSizeQuantities] = useState(initQuantities);
  const [previewSize, setPreviewSize] = useState(initialSettings.size);
  const [isPanelOpen, setIsPanelOpen] = useState(false); // Settings panel visibility - closed by default
  
  // Track if user has clicked "Add to Cart" button
  const hasAddedToCart = React.useRef(false);
  
  // Auto-set quantity to 1 for the preview size when it changes to a size with 0 quantity
  React.useEffect(() => {
    if (previewSize && printSizes[previewSize]) {
      const currentQty = sizeQuantities[previewSize] || 0;
      if (currentQty === 0) {
        setSizeQuantities(prev => ({
          ...prev,
          [previewSize]: 1
        }));
        console.log(`📸 Auto-set quantity to 1 for ${printSizes[previewSize]?.name}`);
      }
    }
  }, [previewSize, printSizes]); // Run when previewSize changes
  
  // Always default to landscape unless explicitly set
  // Users can choose portrait mode with the button
  const initialOrientation = initialSettings.aspectOrientation || 'landscape';
  // Note: This log shows INITIAL settings, not current previewSize
  console.log(`🎯 Initial orientation: ${initialOrientation} (initial size was: ${initialSettings.size}, current previewSize: ${previewSize})`);
  
  const [aspectOrientation, setAspectOrientation] = useState(initialOrientation);
  
  // Memoized quantity change handler to prevent re-renders
  const handleQuantityChange = useCallback((key, newQty) => {
    const prevQty = sizeQuantities[key] || 0;
    
    // If quantity is being set to 0, show confirmation toast
    if (newQty === 0 && prevQty > 0) {
      const sizeName = printSizes[key]?.name || key;
      
      toast(t('toast.remove_size_title', 'Remove this size?'), {
        description: t('toast.remove_size_message', 'Setting quantity to 0 will remove {size} from your order.', { size: sizeName }),
        action: {
          label: t('toast.remove_size_confirm', 'Remove'),
          onClick: () => {
            setSizeQuantities(prev => ({
              ...prev,
              [key]: 0
            }));
            toast.success(t('toast.size_removed', '{size} removed from order', { size: sizeName }));
          }
        },
        cancel: {
          label: t('toast.remove_size_cancel', 'Cancel'),
          onClick: () => {
            setSizeQuantities(prev => ({
              ...prev,
              [key]: prevQty
            }));
            toast.info(t('toast.quantity_kept', 'Quantity restored to {qty}', { qty: prevQty }));
          }
        },
        duration: 5000
      });
      return;
    }
    
    // Normal quantity change
    setSizeQuantities(prev => ({
      ...prev,
      [key]: newQty
    }));
  }, [sizeQuantities, printSizes, t]);
  
  // Crop states for react-easy-crop
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isInitialized, setIsInitialized] = useState(true);  // Default to true, set false when loading saved crops
  
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingCloseCallback, setPendingCloseCallback] = useState(null);
  const isDiscardingChanges = React.useRef(false);  // Flag to prevent saves during discard
  
  // Flag to prevent Cropper's onCropChange from overwriting restored crop position
  const isRestoringCropRef = React.useRef(false);
  const restoredCropRef = React.useRef(null);  // Store the crop we want to restore
  
  // Flag to prevent auto-thumbnail generation until user explicitly saves
  const userHasInteracted = React.useRef(false);
  
  // Wrapper for setCrop that respects restoration flag
  const handleCropChange = React.useCallback((newCrop) => {
    if (isRestoringCropRef.current && restoredCropRef.current) {
      // During restoration, use the saved crop position instead of Cropper's initial position
      console.log(`🛡️ Blocking Cropper reset during restoration. Using saved crop:`, restoredCropRef.current);
      setCrop(restoredCropRef.current);
      return;
    }
    // Mark that user has interacted when they move the crop
    userHasInteracted.current = true;
    setCrop(newCrop);
  }, []);
  
  // Store crops per size - each size can have its own crop
  const [cropsPerSize, setCropsPerSize] = useState({});
  
  // Store crops per image - preserve crops when navigating between images
  // Structure: { imageId: { sizeKey: {crop, zoom, croppedAreaPixels, aspectOrientation} } }
  const [cropsPerImage, setCropsPerImage] = useState(() => {
    // Load any saved crop data from localStorage
    try {
      const savedCrops = JSON.parse(localStorage.getItem('tempCropData') || '{}');
      console.log('📂 Loaded saved crop data from localStorage:', savedCrops);
      return savedCrops;
    } catch (error) {
      console.error('Failed to load saved crop data:', error);
      return {};
    }
  });
  
  // Store quantities per image - preserve quantities when navigating between images
  // Structure: { imageId: { sizeKey: quantity } }
  const [quantitiesPerImage, setQuantitiesPerImage] = useState({});

  // Auto-save crop state when navigating away (preserve work without adding to cart)
  // DISABLED - This was causing orientation corruption across all images
  // TODO: Fix the save logic to only save when user explicitly saves
  /*
  useEffect(() => {
    const saveCurrentCropState = () => {
      if (!image) return;
      
      const imageKey = image.filename; // Always use filename as key for consistency
      const savedCrops = JSON.parse(localStorage.getItem('tempCropData') || '{}');
      
      // Save current crop state for this image and size with orientation-specific key
      if (!savedCrops[imageKey]) savedCrops[imageKey] = {};
      
      // Only save if we have valid crop data (user has interacted with cropper)
      if (!croppedAreaPixels) {
        console.log(`⏭️ Auto-save skip for ${imageKey}: no crop data yet`);
        return;
      }
      
      // Save with orientation-specific key for proper restoration
      const sizeOrientationKey = `${previewSize}-${aspectOrientation}`;
      savedCrops[imageKey][sizeOrientationKey] = {
        crop,
        zoom,
        croppedAreaPixels,
        aspectOrientation
      };
      
      // Don't overwrite the plain key unconditionally - only if it matches current orientation
      // This prevents forcing one orientation on images that haven't been explicitly edited
      const existingPlainData = savedCrops[imageKey][previewSize];
      if (!existingPlainData || existingPlainData.aspectOrientation === aspectOrientation) {
        savedCrops[imageKey][previewSize] = {
          crop,
          zoom,
          croppedAreaPixels,
          aspectOrientation
        };
      }
      
      localStorage.setItem('tempCropData', JSON.stringify(savedCrops));
      console.log(`💾 Auto-saved crop state for ${imageKey}: ${sizeOrientationKey}, orientation=${aspectOrientation}, zoom=${zoom}`);
    };

    // Save crop state on unmount (when modal closes)
    return () => {
      saveCurrentCropState();
    };
  }, [image, previewSize, crop, zoom, croppedAreaPixels, aspectOrientation]);
  */
  
  // Save current crop when preview size changes (before switching)
  const previousPreviewSize = React.useRef(previewSize);
  const previousCrop = React.useRef({ crop, zoom, croppedAreaPixels, aspectOrientation });
  
  // Update the ref whenever these values change
  useEffect(() => {
    previousCrop.current = { crop, zoom, croppedAreaPixels, aspectOrientation };
    console.log(`📸 Crop state updated:`, { 
      crop, 
      zoom, 
      hasCroppedAreaPixels: !!croppedAreaPixels,
      croppedAreaPixels,
      aspectOrientation
    });
  }, [crop, zoom, croppedAreaPixels, aspectOrientation]);
  
  // REMOVED: Auto-save with debounce - User now manually saves with scissors button
  // This was causing issues when users were still adjusting crops
  
  // Save crops and quantities when switching images
  useEffect(() => {
    return () => {
      // On unmount or image change, save current image's crops and quantities
      // BUT NOT if user is discarding changes
      if (!isCartEdit && image.id && !isDiscardingChanges.current) {
        setCropsPerImage(prev => ({
          ...prev,
          [image.id]: cropsPerSize
        }));
        setQuantitiesPerImage(prev => ({
          ...prev,
          [image.id]: sizeQuantities
        }));
      }
    };
  }, [currentImageIndex, cropsPerSize, sizeQuantities, image.id, isCartEdit]);
  
  // Load crops and quantities when image changes
  useEffect(() => {
    if (isCartEdit && initialSettings) {
      // For cart edit mode, load saved aspect orientation
      // Reset initialization to reload crops
      setIsInitialized(false);
      
      // Build cropsPerSize from all cart items for this image
      console.log(`🛒 Cart Edit Mode: Initializing for ${initialSettings.size}`);
      
      // First, check if we have saved crops in localStorage for this image
      const imageKey = image.filename; // Always use filename as key for consistency
      console.log(`🔑 Image key for loading: "${imageKey}" (id: "${image.id}", filename: "${image.filename}")`);
      
      const localStorageCrops = cropsPerImage[imageKey] || {};
      
      console.log(`📦 LocalStorage crops for ${imageKey}:`, Object.keys(localStorageCrops));
      
      // Load saved crops from all cart items for this image, merging with localStorage
      const savedCrops = {};
      if (data.cartQuantities && data.cartItemIds) {
        Object.keys(data.cartQuantities).forEach(sizeKey => {
          // Find the cart item for this size - check both sku and size for compatibility
          const cartItem = cartItems?.find(item => 
            item.filename === image.filename && 
            (item.sku === sizeKey || item.size === sizeKey)
          );
          
          if (cartItem) {
            // Build orientation-specific key to look up saved crop
            const orientation = cartItem.aspectOrientation || 'landscape';
            const sizeOrientationKey = `${sizeKey}-${orientation}`;
            
            // Check localStorage for this size+orientation's crop data first
            const localCrop = localStorageCrops[sizeOrientationKey] || localStorageCrops[sizeKey]; // Fallback to old format
            
            // Priority: localStorage > cartItem.cropPosition > default
            const cropPosition = localCrop?.crop || cartItem.cropPosition || { x: 0, y: 0 };
            const zoomValue = localCrop?.zoom || cartItem.zoom || 1;
            
            console.log(`🔍 Cart item ${sizeKey}: localStorage=${localCrop ? 'YES' : 'NO'}, cartItem.cropPosition:`, cartItem.cropPosition, ', zoom:', zoomValue);
            
            savedCrops[sizeKey] = {
              aspectOrientation: orientation,
              crop: cropPosition,
              zoom: zoomValue,
              croppedAreaPixels: localCrop?.croppedAreaPixels || null
            };
          }
        });
      }
      
      setCropsPerSize(savedCrops);
      
      // Get the preview size key - use SKU if available
      const previewSizeKey = initialSettings.size;
      console.log(`🎯 Cart Edit: previewSize=${previewSize}, initialSettings.size=${initialSettings.size}, savedCrops keys:`, Object.keys(savedCrops));
      
      // Load saved crop position for the current preview size if available
      // savedCrops is keyed by size (e.g., "P64"), not size-orientation
      if (savedCrops[previewSizeKey]) {
        const savedCrop = savedCrops[previewSizeKey];
        console.log(`📂 Cart Edit: Restoring crop for ${previewSizeKey}:`, {
          crop: savedCrop.crop,
          zoom: savedCrop.zoom,
          aspectOrientation: savedCrop.aspectOrientation
        });
        
        // Enable restoration protection before setting crop
        const cropToRestore = savedCrop.crop || { x: 0, y: 0 };
        isRestoringCropRef.current = true;
        restoredCropRef.current = cropToRestore;
        setCrop(cropToRestore);
        setZoom(savedCrop.zoom || 1);
        setAspectOrientation(savedCrop.aspectOrientation || 'landscape');
        setCroppedAreaPixels(savedCrop.croppedAreaPixels || null);
        
        // Clear restoration flag after a delay to allow Cropper to initialize
        setTimeout(() => {
          isRestoringCropRef.current = false;
          restoredCropRef.current = null;
          console.log(`🔓 Restoration protection cleared for ${previewSizeKey}`);
        }, 500);
      } else {
        // Fallback: check if initialSettings has aspectOrientation directly from cart item
        console.log(`⚠️ Cart Edit: No saved crop found for ${previewSizeKey}, using initialSettings.aspectOrientation: ${initialSettings.aspectOrientation}`);
        setAspectOrientation(initialSettings.aspectOrientation || 'landscape');
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
      }
      
      setIsInitialized(true);  // Mark as initialized
      
    } else if (!isCartEdit && image.id && cropsPerImage[image.id]) {
      setCropsPerSize(cropsPerImage[image.id]);
      console.log(`📂 Loaded saved crops for image ${image.filename}`);
      
      // Load the first saved size's settings if available
      const firstSavedSize = Object.keys(cropsPerImage[image.id])[0];
      if (firstSavedSize && cropsPerImage[image.id][firstSavedSize]) {
        const saved = cropsPerImage[image.id][firstSavedSize];
        const cropToRestore = saved.crop || { x: 0, y: 0 };
        
        // Enable restoration protection
        isRestoringCropRef.current = true;
        restoredCropRef.current = cropToRestore;
        setCrop(cropToRestore);
        setZoom(saved.zoom || 1);
        setAspectOrientation(saved.aspectOrientation || 'landscape');
        
        setTimeout(() => {
          isRestoringCropRef.current = false;
          restoredCropRef.current = null;
          console.log(`🔓 Restoration protection cleared for ${firstSavedSize}`);
        }, 500);
      }
    } else if (!isCartEdit) {
      // Check if this image has items in cart - load their state
      const imageCartItems = cartItems?.filter(item => item.filename === image.filename) || [];
      
      if (imageCartItems.length > 0) {
        // Try to load from localStorage first (has full crop data including position)
        const imageKey = image.filename; // Always use filename as key for consistency
        const localStorageCrops = cropsPerImage[imageKey];
        
        // Load saved state from cart items and merge with localStorage if available
        const savedCrops = {};
        imageCartItems.forEach(cartItem => {
          // Use SKU as the key for consistency with printSizes
          const cartKey = cartItem.sku || cartItem.size;
          const localCrop = localStorageCrops?.[cartKey];
          savedCrops[cartKey] = {
            aspectOrientation: cartItem.aspectOrientation || 'landscape',
            crop: localCrop?.crop || cartItem.cropPosition || { x: 0, y: 0 },  // Restore from localStorage or cart
            zoom: localCrop?.zoom || cartItem.zoom || 1,  // Prefer localStorage, fallback to cart
            croppedAreaPixels: localCrop?.croppedAreaPixels || null
          };
        });
        
        setCropsPerSize(savedCrops);
        
        // Set initial state from first cart item or localStorage
        const firstItem = imageCartItems[0];
        const firstKey = firstItem.sku || firstItem.size;
        const firstCrop = savedCrops[firstKey];
        const cropToRestore = firstCrop.crop;
        
        // Enable restoration protection
        isRestoringCropRef.current = true;
        restoredCropRef.current = cropToRestore;
        
        setAspectOrientation(firstItem.aspectOrientation || 'landscape');
        setCrop(cropToRestore);
        setZoom(firstCrop.zoom);
        setCroppedAreaPixels(firstCrop.croppedAreaPixels);
        console.log(`📦 Loaded state from ${imageCartItems.length} cart items for ${image.filename}, restored crop position:`, firstCrop.crop);
        
        setTimeout(() => {
          isRestoringCropRef.current = false;
          restoredCropRef.current = null;
          console.log(`🔓 Restoration protection cleared for cart items`);
        }, 500);
        
        setIsInitialized(true);  // Mark as initialized
      } else {
        // New image, check if we have saved crop data in localStorage first
        const imageKey = image.filename; // Always use filename as key for consistency
        const savedCrops = cropsPerImage[imageKey];
        
        // FIRST: Check if image has croppedThumbnails with orientation (this is the source of truth for grid)
        const croppedThumbObj = image.croppedThumbnails?.[previewSize];
        const gridOrientation = typeof croppedThumbObj === 'object' ? croppedThumbObj.aspectOrientation : null;
        
        console.log(`🔍 Checking orientation sources: gridOrientation=${gridOrientation}, savedCrops keys=`, savedCrops ? Object.keys(savedCrops) : 'none');
        
        if (savedCrops && Object.keys(savedCrops).length > 0) {
          // Has saved crops - reload them
          setIsInitialized(false);
          
          // Restore previously saved crops from localStorage
          setCropsPerSize(savedCrops);
          console.log(`💾 Restored saved crops from localStorage for ${imageKey}:`, savedCrops);
          
          // Use grid orientation as priority (what user sees), then check localStorage keys
          const landscapeKey = `${previewSize}-landscape`;
          const portraitKey = `${previewSize}-portrait`;
          
          let savedCrop = null;
          let usedKey = null;
          
          // Priority 1: If grid shows a specific orientation, use that key
          if (gridOrientation === 'portrait' && savedCrops[portraitKey]) {
            savedCrop = savedCrops[portraitKey];
            usedKey = portraitKey;
            console.log(`📌 Using portrait key from grid orientation`);
          } else if (gridOrientation === 'landscape' && savedCrops[landscapeKey]) {
            savedCrop = savedCrops[landscapeKey];
            usedKey = landscapeKey;
            console.log(`📌 Using landscape key from grid orientation`);
          } 
          // Priority 2: Plain key (might have the actual saved orientation)
          else if (savedCrops[previewSize]) {
            savedCrop = savedCrops[previewSize];
            usedKey = previewSize;
            console.log(`📌 Using plain key`);
          }
          // Priority 3: Any orientation-specific key that exists
          else if (savedCrops[portraitKey]) {
            savedCrop = savedCrops[portraitKey];
            usedKey = portraitKey;
            console.log(`📌 Using portrait key (fallback)`);
          } else if (savedCrops[landscapeKey]) {
            savedCrop = savedCrops[landscapeKey];
            usedKey = landscapeKey;
            console.log(`📌 Using landscape key (fallback)`);
          }
          
          if (savedCrop) {
            const cropToRestore = savedCrop.crop || { x: 0, y: 0 };
            // Use the orientation from grid if available, otherwise from saved crop
            const orientationToRestore = gridOrientation || savedCrop.aspectOrientation || 'landscape';
            const zoomToRestore = savedCrop.zoom || 1;
            
            // Enable restoration protection
            isRestoringCropRef.current = true;
            restoredCropRef.current = cropToRestore;
            
            setCrop(cropToRestore);
            setZoom(zoomToRestore);
            setAspectOrientation(orientationToRestore);
            setCroppedAreaPixels(savedCrop.croppedAreaPixels || null);
            console.log(`🔄 Restored crop state for ${usedKey}: orientation=${orientationToRestore}, zoom=${zoomToRestore}`, 'crop:', cropToRestore);
            
            setTimeout(() => {
              isRestoringCropRef.current = false;
              restoredCropRef.current = null;
              console.log(`🔓 Restoration protection cleared for ${usedKey}`);
            }, 500);
          }
          
          setIsInitialized(true);  // Mark as initialized
        } else if (gridOrientation) {
          // No localStorage data, but grid has saved thumbnail with orientation
          setCropsPerSize({});
          setCrop({ x: 0, y: 0 });
          setZoom(1);
          setCroppedAreaPixels(null);
          userHasInteracted.current = false;
          isDiscardingChanges.current = false;
          setAspectOrientation(gridOrientation);
          console.log(`🆕 Using grid orientation for ${image.filename}: ${gridOrientation}`);
        } else {
          // Truly new image - detect orientation from image dimensions
          setCropsPerSize({});
          setCrop({ x: 0, y: 0 });
          setZoom(1);
          setCroppedAreaPixels(null);
          userHasInteracted.current = false;  // Reset interaction flag for new image
          isDiscardingChanges.current = false;  // Reset discard flag for new image
          
          // Detect orientation from image dimensions
          const imageData = image.image_data || image.preview || image.thumbnail;
          if (imageData) {
            const img = new Image();
            img.onload = () => {
              const width = img.naturalWidth || img.width;
              const height = img.naturalHeight || img.height;
              const detectedOrientation = height > width ? 'portrait' : 'landscape';
              console.log(`🆕 New image ${image.filename}, detected ${width}x${height} → ${detectedOrientation}`);
              setAspectOrientation(detectedOrientation);
            };
            img.src = imageData.startsWith('data:') ? imageData : `data:image/jpeg;base64,${imageData}`;
          } else {
            console.log(`🆕 New image ${image.filename}, no image data - defaulting to landscape`);
            setAspectOrientation('landscape');
          }
        }
      }
    }
    
    // Load quantities for this image
    if (!isCartEdit && image.id && quantitiesPerImage[image.id]) {
      setSizeQuantities(quantitiesPerImage[image.id]);
      console.log(`📦 Loaded saved quantities for image ${image.filename}`);
    } else if (!isCartEdit) {
      // New image, reset quantities to original cart quantities
      setSizeQuantities(initQuantities);
      console.log(`🆕 Reset quantities for new image ${image.filename}`);
    }
  }, [currentImageIndex, image.id, isCartEdit]);
  
  useEffect(() => {
    console.log(`🔀 Preview size changed: ${previousPreviewSize.current} → ${previewSize}, isCartEdit: ${isCartEdit}`);
    
    // Skip on initial render (when previousPreviewSize.current is null/undefined)
    // But DO process size changes even in cart edit mode
    if (!previousPreviewSize.current) {
      console.log(`⏭️ Initial render - skipping size change effect`);
      previousPreviewSize.current = previewSize;
      return;
    }
    
    // If size didn't actually change, skip
    if (previousPreviewSize.current === previewSize) {
      return;
    }
    
    // AUTO-SAVE current crop state BEFORE switching to new size
    const prevSize = previousPreviewSize.current;
    setCropsPerSize(prevCrops => {
      // Save current state for previous size (if we have crop data)
      const updatedCrops = { ...prevCrops };
      
      // Get current crop state from refs/state to save for the previous size
      // This ensures we don't lose changes when switching sizes
      if (prevSize && !isRestoringCropRef.current) {
        updatedCrops[prevSize] = {
          ...updatedCrops[prevSize],
          crop: crop,
          zoom: zoom,
          aspectOrientation: aspectOrientation,
          croppedAreaPixels: croppedAreaPixels
        };
        console.log(`💾 Auto-saved crop for previous size ${prevSize}:`, {
          crop, zoom, aspectOrientation
        });
      }
      
      return updatedCrops;
    });
    
    // Update the ref
    previousPreviewSize.current = previewSize;
    
    // Load crop for new size if it exists
    setCropsPerSize(prevCrops => {
      // Skip if we're in the middle of restoring from cart items
      if (isRestoringCropRef.current) {
        console.log(`⏭️ Skipping size change effect - restoration in progress`);
        return prevCrops;
      }
      
      if (prevCrops[previewSize]) {
        const savedCrop = prevCrops[previewSize];
        const cropToRestore = savedCrop.crop || { x: 0, y: 0 };
        
        // Enable restoration protection
        isRestoringCropRef.current = true;
        restoredCropRef.current = cropToRestore;
        
        setCrop(cropToRestore);
        setZoom(savedCrop.zoom || 1);
        setCroppedAreaPixels(savedCrop.croppedAreaPixels);
        setAspectOrientation(savedCrop.aspectOrientation || 'landscape');
        console.log(`📂 Loaded saved crop for size ${previewSize}:`, {
          crop: cropToRestore,
          zoom: savedCrop.zoom,
          aspectOrientation: savedCrop.aspectOrientation
        });
        
        setTimeout(() => {
          isRestoringCropRef.current = false;
          restoredCropRef.current = null;
          console.log(`🔓 Restoration protection cleared for size change to ${previewSize}`);
        }, 500);
      } else {
        // No saved crop for this size, reset to defaults
        // Keep the current aspectOrientation (user's choice) instead of forcing based on print size
        
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        // Don't change aspectOrientation - keep user's current choice
        console.log(`🆕 New size ${previewSize}, using default crop with current orientation: ${aspectOrientation}`);
      }
      return prevCrops; // Don't modify the state
    });
  }, [previewSize, printSizes, aspectOrientation, crop, zoom, croppedAreaPixels]);
  
  // Set image source from preview (best quality), image_data, or thumbnail (fallback)
  const getImageSource = () => {
    // Priority 1: Use preview for best quality in modal
    if (image.preview) {
      return `data:image/jpeg;base64,${image.preview}`;
    }
    // Priority 2: Use image_data if available
    if (image.image_data) {
      // If it's already a data URL, use it
      if (image.image_data.startsWith('data:')) {
        return image.image_data;
      }
      // Otherwise assume it's base64 and construct data URL
      return `data:image/jpeg;base64,${image.image_data}`;
    }
    // Priority 3: Fallback to thumbnail (lowest quality)
    if (image.thumbnail) {
      return `data:image/jpeg;base64,${image.thumbnail}`;
    }
    return '';
  };
  // Use state instead of ref to trigger re-renders
  const [currentImageSrc, setCurrentImageSrc] = useState(getImageSource());
  
  // Update currentImageSrc when image changes (navigation between images)
  useEffect(() => {
    const newSrc = getImageSource();
    console.log(`🔄 Image changed, updating src for: ${image.filename}`);
    setCurrentImageSrc(newSrc);
  }, [image.id, currentImageIndex]); // Trigger when image changes

  // Callback when crop area changes
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    // croppedArea has percentages (0-100)
    // croppedAreaPixels has pixel coordinates
    console.log('🔄 onCropComplete called:', { croppedArea, croppedAreaPixels });
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Generate cropped thumbnail whenever crop or zoom changes
  // Use a ref to track last valid croppedAreaPixels for zoom changes
  const lastValidCroppedAreaPixels = React.useRef(null);
  const lastGeneratedZoom = React.useRef(null);
  const thumbnailGenerationTimer = React.useRef(null);
  
  // CRITICAL: Use a ref to track current previewSize to avoid stale closure in setTimeout
  const currentPreviewSizeRef = React.useRef(previewSize);
  
  // Keep the ref in sync with state
  useEffect(() => {
    currentPreviewSizeRef.current = previewSize;
    console.log(`📍 Updated currentPreviewSizeRef to: ${previewSize}`);
  }, [previewSize]);
  
  useEffect(() => {
    console.log(`🔄 Thumbnail effect triggered:`, { 
      hasCroppedAreaPixels: !!croppedAreaPixels, 
      hasLastValid: !!lastValidCroppedAreaPixels.current,
      hasImageSrc: !!currentImageSrc,
      previewSize,
      zoom
    });
    
    // Update last valid value whenever croppedAreaPixels changes
    if (croppedAreaPixels) {
      lastValidCroppedAreaPixels.current = croppedAreaPixels;
    }
    
    // Don't auto-generate thumbnails until user explicitly interacts
    // Also don't generate if user is discarding changes
    if (!userHasInteracted.current || isDiscardingChanges.current) {
      console.log(`⏭️ Skipping auto-thumbnail: user hasn't interacted or is discarding`);
      return;
    }
    
    // Use either current or last valid value
    const pixelsToUse = croppedAreaPixels || lastValidCroppedAreaPixels.current;
    
    if (!pixelsToUse || !currentImageSrc || !previewSize) {
      console.log(`⏭️ Early return from thumbnail effect:`, {
        pixelsToUse: !!pixelsToUse,
        currentImageSrc: !!currentImageSrc,
        previewSize
      });
      return;
    }
    
    // Clear any pending generation
    if (thumbnailGenerationTimer.current) {
      clearTimeout(thumbnailGenerationTimer.current);
    }
    
    // Debounce thumbnail generation to ensure croppedAreaPixels is up-to-date
    thumbnailGenerationTimer.current = setTimeout(async () => {
      try {
        // CRITICAL: Use ref to get the CURRENT previewSize, not the stale closure value
        const currentSize = currentPreviewSizeRef.current;
        
        // Use the CURRENT croppedAreaPixels if available (it should be updated by now)
        const finalPixels = croppedAreaPixels || lastValidCroppedAreaPixels.current;
        if (!finalPixels) {
          console.log(`⚠️ No croppedAreaPixels available for thumbnail generation`);
          return;
        }
        
        // Force re-generation if zoom changed
        const needsRegeneration = lastGeneratedZoom.current !== zoom;
        
        console.log(`🎨 Generating thumbnail for ${currentSize}:`, {
          zoom,
          aspectOrientation,
          pixelsWidth: finalPixels.width,
          pixelsHeight: finalPixels.height,
          needsRegeneration,
          lastZoom: lastGeneratedZoom.current
        });
        
        // Convert croppedAreaPixels to percentages
        const img = new Image();
        img.src = currentImageSrc;
        await new Promise((resolve) => { img.onload = resolve; });
        
        const originalWidth = img.naturalWidth || img.width;
        const originalHeight = img.naturalHeight || img.height;
        
        const cropSettings = {
          x: (finalPixels.x / originalWidth) * 100,
          y: (finalPixels.y / originalHeight) * 100,
          width: (finalPixels.width / originalWidth) * 100,
          height: (finalPixels.height / originalHeight) * 100
        };
        
        // Use currentSize (from ref) instead of previewSize (from closure)
        const sizeConfig = printSizes[currentSize];
        console.log(`📏 Using sizeConfig for ${currentSize}:`, {
          name: sizeConfig?.name,
          ratioX: sizeConfig?.crop_ratio_x,
          ratioY: sizeConfig?.crop_ratio_y,
          aspectOrientation
        });
        const croppedThumb = await generateCroppedThumbnail(
          currentImageSrc,
          cropSettings,
          0, // No rotation
          400,
          {
            ratioX: sizeConfig?.crop_ratio_x || 3,
            ratioY: sizeConfig?.crop_ratio_y || 2,
            orientation: aspectOrientation
          },
          zoom || 1
        );
        
        // Update cropsPerSize with the generated thumbnail - use currentSize from ref
        setCropsPerSize(prev => ({
          ...prev,
          [currentSize]: {
            crop,
            zoom,
            croppedAreaPixels: finalPixels,
            aspectOrientation,
            croppedThumbnail: croppedThumb
          }
        }));
        
        // Store the generated thumbnail locally for preview in cropper's thumbnail strip
        // But DON'T update the parent grid - only do that on explicit Save/Add to Cart
        
        // Track what we generated (for local preview only)
        lastGeneratedZoom.current = zoom;
        
        console.log(`✅ Generated local preview thumbnail for ${currentSize} (not saved to grid yet)`);
      } catch (error) {
        console.error('Failed to generate thumbnail:', error);
      }
    }, 300); // 300ms debounce to allow Cropper to update croppedAreaPixels
    
    // Cleanup
    return () => {
      if (thumbnailGenerationTimer.current) {
        clearTimeout(thumbnailGenerationTimer.current);
      }
    };
  }, [croppedAreaPixels, previewSize, currentImageSrc, crop, zoom, aspectOrientation]);
  // Note: printSizes, image, onUpdateThumbnail removed from deps to prevent infinite loop
  
  // Toggle aspect ratio orientation
  // Portrait/Landscape buttons control the CROP BOX shape
  const toggleOrientation = (orientation) => {
    console.log(`📐 Changing crop box to ${orientation} orientation`);
    setAspectOrientation(orientation);
  };

  // Get dynamic aspect ratio based on orientation
  const getAspectRatio = () => {
    const ratioX = printSizes[previewSize]?.crop_ratio_x || 3;
    const ratioY = printSizes[previewSize]?.crop_ratio_y || 2;
    
    if (aspectOrientation === 'portrait') {
      return `${ratioY} / ${ratioX}`; // Flip the ratio
    }
    return `${ratioX} / ${ratioY}`; // Normal landscape
  };
  
  // Get aspect ratio for react-easy-crop (width / height of crop box)
  const getAspectRatioNumber = () => {
    const ratioX = printSizes[previewSize]?.crop_ratio_x || 3;
    const ratioY = printSizes[previewSize]?.crop_ratio_y || 2;
    
    // Determine if this print size is naturally landscape or portrait
    // 6x4 (ratioX=6, ratioY=4) is naturally landscape (6 > 4)
    // 8x10 (ratioX=8, ratioY=10) is naturally portrait (8 < 10)
    const isNaturallyLandscape = ratioX > ratioY;
    
    // For react-easy-crop aspect prop = width / height of crop box
    let aspectRatio;
    if (aspectOrientation === 'portrait') {
      // Portrait: taller (aspect < 1)
      aspectRatio = isNaturallyLandscape 
        ? ratioY / ratioX   // 6x4 portrait: 4/6 = 0.667
        : ratioX / ratioY;  // 8x10 portrait: 8/10 = 0.8
    } else {
      // Landscape: wider (aspect > 1)
      aspectRatio = isNaturallyLandscape
        ? ratioX / ratioY   // 6x4 landscape: 6/4 = 1.5
        : ratioY / ratioX;  // 8x10 landscape: 10/8 = 1.25
    }
    
    console.log(`📐 ${previewSize} (${ratioX}:${ratioY}, naturally ${isNaturallyLandscape ? 'landscape' : 'portrait'}) → ${aspectOrientation} → aspect = ${aspectRatio.toFixed(3)}`);
    
    return aspectRatio;
  };
  
  // Initialize croppedAreaPixels when modal opens
  // This ensures the "Save Crop" button works even if user doesn't interact with the crop
  React.useEffect(() => {
    // Create a small timeout to let the Cropper component initialize
    const timer = setTimeout(() => {
      // If croppedAreaPixels is still null after component mounts, trigger a manual calculation
      if (!croppedAreaPixels) {
        console.log('📐 Initializing crop area on mount...');
        // The Cropper will call onCropComplete automatically once it's rendered
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Undo all changes - reset everything to initial state
  const handleUndo = () => {
    if (confirm('Reset all changes to this image?')) {
      setCurrentImageSrc(getImageSource());
      setAspectOrientation('landscape');
      setZoom(1);  // Reset zoom
      setCrop({ x: 0, y: 0 });  // Reset pan position
      setCroppedAreaPixels(null);  // Clear crop area to trigger recalculation
      lastValidCroppedAreaPixels.current = null;  // Clear the ref too
      console.log('🔄 Reset: orientation=landscape, zoom=1, crop={x:0,y:0}');
    }
  };

  // Calculate total price across all sizes
  const calculateTotal = () => {
    let total = 0;
    Object.entries(sizeQuantities).forEach(([sizeKey, qty]) => {
      if (qty > 0) {
        total += (printSizes[sizeKey]?.price || 0) * qty;
      }
    });
    return total;
  };

  const totalPrice = calculateTotal();
  const totalQuantity = Object.values(sizeQuantities).reduce((sum, qty) => sum + qty, 0);

  // Helper function to save current crop state to localStorage
  const saveCurrentCropState = React.useCallback(() => {
    if (!image) return;
    
    // Only save if we have valid crop data (user has interacted with cropper)
    if (!croppedAreaPixels) {
      console.log(`⏭️ Skip save for ${image.filename}: no crop data yet`);
      return;
    }
    
    const imageKey = image.filename;
    const savedCrops = JSON.parse(localStorage.getItem('tempCropData') || '{}');
    if (!savedCrops[imageKey]) savedCrops[imageKey] = {};
    
    // Only save with orientation-specific key to avoid overwriting other orientations
    const sizeOrientationKey = `${previewSize}-${aspectOrientation}`;
    const cropData = {
      crop,
      zoom,
      croppedAreaPixels,
      aspectOrientation
    };
    savedCrops[imageKey][sizeOrientationKey] = cropData;
    
    // Don't overwrite the plain key if a different orientation already exists there
    // This prevents forcing one orientation on images that haven't been explicitly edited
    const existingPlainData = savedCrops[imageKey][previewSize];
    if (!existingPlainData || existingPlainData.aspectOrientation === aspectOrientation) {
      savedCrops[imageKey][previewSize] = cropData;
    }
    
    localStorage.setItem('tempCropData', JSON.stringify(savedCrops));
    console.log(`💾 Saved crop state for ${imageKey}: ${sizeOrientationKey}, orientation=${aspectOrientation}, zoom=${zoom}`);
  }, [image, previewSize, aspectOrientation, crop, zoom, croppedAreaPixels]);

  // Navigate to previous image (with save)
  const handlePrevImage = React.useCallback(() => {
    if (currentImageIndex > 0) {
      saveCurrentCropState();
      setCurrentImageIndex(currentImageIndex - 1);
    }
  }, [currentImageIndex, saveCurrentCropState]);

  // Navigate to next image (with save)
  const handleNextImage = React.useCallback(() => {
    if (currentImageIndex < allImages.length - 1) {
      saveCurrentCropState();
      setCurrentImageIndex(currentImageIndex + 1);
    }
  }, [currentImageIndex, allImages.length, saveCurrentCropState]);

  // Log image source on mount for debugging
  React.useEffect(() => {
    console.log('ImageEditorModal - currentImageSrc:', currentImageSrc ? `${currentImageSrc.substring(0, 50)}...` : 'EMPTY');
    console.log('ImageEditorModal - image.image_data:', image.image_data ? 'EXISTS' : 'MISSING');
    console.log('ImageEditorModal - image.thumbnail:', image.thumbnail ? 'EXISTS' : 'MISSING');
    if (!currentImageSrc) {
      console.error('ImageEditorModal - NO IMAGE SOURCE FOUND!');
    }
  }, [currentImageSrc]);

  // Handle confirmation dialog for unsaved changes
  const handleCloseWithConfirmation = (callback) => {
    // Check if user has made changes
    const hasMadeChanges = userHasInteracted.current && croppedAreaPixels !== null;
    
    if (hasMadeChanges) {
      // Store the callback and show dialog
      setPendingCloseCallback(() => callback);
      setShowConfirmDialog(true);
    } else {
      // No changes made, just close
      callback();
    }
  };
  
  // Handle save and close from confirmation dialog
  const handleConfirmSave = async () => {
    console.log(`💾 User chose to save crop settings for ${image?.filename}`);
    console.log(`📊 Save state: croppedAreaPixels=${!!croppedAreaPixels}, currentImageSrc=${!!currentImageSrc}, previewSize=${previewSize}`);
    
    // Generate and save thumbnail
    if (!croppedAreaPixels) {
      console.warn('⚠️ No croppedAreaPixels available for save');
    }
    if (!currentImageSrc) {
      console.warn('⚠️ No currentImageSrc available for save');
    }
    
    if (croppedAreaPixels && currentImageSrc) {
      try {
        const sizeConfig = printSizes[previewSize];
        console.log(`🔧 Generating thumbnail with sizeConfig:`, sizeConfig);
        
        // Calculate crop settings as percentages from croppedAreaPixels
        const img = new Image();
        img.src = currentImageSrc.startsWith('data:') ? currentImageSrc : `data:image/jpeg;base64,${currentImageSrc}`;
        
        await new Promise((resolve) => {
          img.onload = resolve;
        });
        
        const originalWidth = img.naturalWidth || img.width;
        const originalHeight = img.naturalHeight || img.height;
        
        const cropSettings = {
          x: (croppedAreaPixels.x / originalWidth) * 100,
          y: (croppedAreaPixels.y / originalHeight) * 100,
          width: (croppedAreaPixels.width / originalWidth) * 100,
          height: (croppedAreaPixels.height / originalHeight) * 100
        };
        
        console.log(`📐 Calculated crop settings:`, cropSettings);
        
        const thumbnailBase64 = await generateCroppedThumbnail(
          currentImageSrc,
          cropSettings,
          0, // No rotation
          400,
          {
            ratioX: sizeConfig?.crop_ratio_x || 3,
            ratioY: sizeConfig?.crop_ratio_y || 2,
            orientation: aspectOrientation
          },
          zoom || 1
        );
        
        console.log(`🖼️ Generated thumbnail: ${thumbnailBase64 ? 'SUCCESS' : 'FAILED'}, length=${thumbnailBase64?.length || 0}`);
        
        // Save crop settings to localStorage AND in-memory state
        if (image) {
          const imageKey = image.filename;
          const sizeOrientationKey = `${previewSize}-${aspectOrientation}`;
          const cropData = {
            crop,
            zoom,
            croppedAreaPixels,
            aspectOrientation
          };
          
          // Update localStorage
          const savedCrops = JSON.parse(localStorage.getItem('tempCropData') || '{}');
          if (!savedCrops[imageKey]) savedCrops[imageKey] = {};
          savedCrops[imageKey][sizeOrientationKey] = cropData;
          // Also save with plain key for easier lookup
          savedCrops[imageKey][previewSize] = cropData;
          localStorage.setItem('tempCropData', JSON.stringify(savedCrops));
          
          // Update in-memory cropsPerImage state so it's available on reopen
          setCropsPerImage(prev => ({
            ...prev,
            [imageKey]: {
              ...(prev[imageKey] || {}),
              [sizeOrientationKey]: cropData,
              [previewSize]: cropData
            }
          }));
          
          console.log(`💾 Saved crop settings to localStorage AND state for ${imageKey} - ${previewSize}, zoom=${zoom}`);
        }
        
        // Update the grid thumbnail
        console.log(`🔍 onUpdateThumbnail available: ${!!onUpdateThumbnail}, image: ${!!image}`);
        if (thumbnailBase64 && onUpdateThumbnail && image) {
          const ratioId = sizeConfig 
            ? `${sizeConfig.crop_ratio_x}:${sizeConfig.crop_ratio_y}`
            : previewSize;
            
          onUpdateThumbnail(image.filename, previewSize, {
            id: `${image.filename}_${ratioId}_${Date.now()}`,
            data: thumbnailBase64,
            ratio: ratioId,
            aspectOrientation: aspectOrientation,
            timestamp: Date.now()
          });
          console.log(`✅ Saved thumbnail to grid for ${image.filename} - ${previewSize}`);
        } else {
          console.warn(`⚠️ Could not update grid: thumbnailBase64=${!!thumbnailBase64}, onUpdateThumbnail=${!!onUpdateThumbnail}, image=${!!image}`);
        }
      } catch (error) {
        console.error('Error saving thumbnail:', error);
      }
    }
    
    setShowConfirmDialog(false);
    if (pendingCloseCallback) {
      pendingCloseCallback();
      setPendingCloseCallback(null);
    }
  };
  
  // Handle discard from confirmation dialog
  const handleConfirmDiscard = () => {
    console.log(`🗑️ User chose to discard crop settings for ${image?.filename}`);
    isDiscardingChanges.current = true;  // Prevent any saves
    userHasInteracted.current = false;  // Reset so no auto-save happens
    
    // Clear localStorage data for this image to prevent loading discarded zoom/crop
    if (image?.filename) {
      try {
        const savedCrops = JSON.parse(localStorage.getItem('tempCropData') || '{}');
        if (savedCrops[image.filename]) {
          delete savedCrops[image.filename];
          localStorage.setItem('tempCropData', JSON.stringify(savedCrops));
          console.log(`🧹 Cleared localStorage crop data for ${image.filename}`);
        }
      } catch (error) {
        console.error('Failed to clear localStorage crop data:', error);
      }
      
      // Also clear in-memory cropsPerImage state for this image
      setCropsPerImage(prev => {
        const updated = { ...prev };
        delete updated[image.filename];
        return updated;
      });
    }
    
    // Reset crop state to defaults to prevent any saves with old values
    setCropsPerSize({});
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    
    setShowConfirmDialog(false);
    if (pendingCloseCallback) {
      pendingCloseCallback();
      setPendingCloseCallback(null);
    }
  };

  const handleSave = () => {
    // Save current crop for the current preview size before processing
    // This ensures even unsaved crops are included when adding to cart
    const currentCropData = {
      crop,
      zoom,
      croppedAreaPixels,
      aspectOrientation
    };
    
    // Update cropsPerSize with current crop if it exists
    if (croppedAreaPixels) {
      setCropsPerSize(prev => ({
        ...prev,
        [previewSize]: currentCropData
      }));
      console.log(`💾 Auto-saving current crop before adding to cart:`, currentCropData);
    }
    
    // Mark that user has added to cart - prevents confirmation dialog
    hasAddedToCart.current = true;
    
    // Helper function to convert pixel crop to percentage crop
    const convertCropToPercentages = (croppedPixels) => {
      if (!croppedPixels) return null;
      
      // Get original image dimensions
      const img = new Image();
      img.src = currentImageSrc;
      const originalWidth = img.naturalWidth || img.width;
      const originalHeight = img.naturalHeight || img.height;
      
      if (originalWidth && originalHeight) {
        return {
          x: (croppedPixels.x / originalWidth) * 100,
          y: (croppedPixels.y / originalHeight) * 100,
          width: (croppedPixels.width / originalWidth) * 100,
          height: (croppedPixels.height / originalHeight) * 100
        };
      }
      return null;
    };
    
    // Calculate the DIFFERENCE between new quantities and original cart quantities
    const itemsToAdd = [];
    Object.entries(sizeQuantities).forEach(([size, newQuantity]) => {
      const originalQuantity = originalCartQuantities[size] || 0;
      const quantityDifference = newQuantity - originalQuantity;
      
      // Get the crop for THIS specific size (either saved or current if it's the preview size)
      let sizeCropSettings = null;
      if (size === previewSize && croppedAreaPixels) {
        // Use current crop for preview size
        sizeCropSettings = convertCropToPercentages(croppedAreaPixels);
      } else if (cropsPerSize[size]?.croppedAreaPixels) {
        // Use saved crop for this size
        sizeCropSettings = convertCropToPercentages(cropsPerSize[size].croppedAreaPixels);
      } else {
        // Default: Calculate centered crop that respects target aspect ratio
        // This mimics what the cropper shows by default
        const sizeConfig = printSizes[size];
        if (sizeConfig?.crop_ratio_x && sizeConfig?.crop_ratio_y) {
          const targetRatio = sizeConfig.crop_ratio_x / sizeConfig.crop_ratio_y;
          
          // Assume image is roughly 1:1 or use a safe default
          // We calculate a centered crop that fills the target ratio
          const imageAspect = 1; // We don't know actual image dimensions here, assume square
          
          let cropWidth, cropHeight, cropX, cropY;
          
          if (imageAspect > targetRatio) {
            // Image is wider than target - crop sides
            cropHeight = 100;
            cropWidth = (targetRatio / imageAspect) * 100;
            cropX = (100 - cropWidth) / 2;
            cropY = 0;
          } else {
            // Image is taller than target - crop top/bottom
            cropWidth = 100;
            cropHeight = (imageAspect / targetRatio) * 100;
            cropX = 0;
            cropY = (100 - cropHeight) / 2;
          }
          
          sizeCropSettings = { 
            x: cropX, 
            y: cropY, 
            width: cropWidth, 
            height: cropHeight 
          };
          console.log(`📐 Using calculated centered crop for ${size}:`, sizeCropSettings);
        } else {
          // Fallback if no size config
          sizeCropSettings = { x: 0, y: 0, width: 100, height: 100 };
          console.log(`⚠️ Using fallback full-image crop for ${size} (no size config)`);
        }
      }
      
      // Get aspect orientation for this size
      const sizeAspectOrientation = (size === previewSize) 
        ? aspectOrientation 
        : (cropsPerSize[size]?.aspectOrientation || 'landscape');
      
      // Get zoom for this specific size
      const sizeZoom = (size === previewSize)
        ? zoom
        : (cropsPerSize[size]?.zoom || 1);
      
      // Get crop position (pan) for this specific size
      const sizeCropPosition = (size === previewSize)
        ? crop
        : (cropsPerSize[size]?.crop || { x: 0, y: 0 });
      
      // Get product info for display
      const product = printSizes[size];
      
      // Add item if quantity changed
      if (quantityDifference !== 0 || newQuantity > 0) {
        itemsToAdd.push({
          sku: size,  // SKU is the primary key (size variable is now the SKU)
          size: product?.size || size,  // Display size like "6x4"
          // Note: paper_type and border will be selected in Order Summary modal during checkout
          quantity: isCartEdit ? newQuantity : quantityDifference, // Cart edit: use absolute quantity, New: use difference
          zoom: sizeZoom, // Use size-specific zoom
          aspectOrientation: sizeAspectOrientation, // Save orientation for cart display
          crop_settings: sizeCropSettings, // Save crop data as percentages (unique per size!)
          cropPosition: sizeCropPosition, // Save pan position for restoration when re-editing
          forceUpdate: false,
          isCartEdit: isCartEdit, // Flag to indicate this is an edit, not an addition
          cartItemId: isCartEdit ? data.cartItemIds?.[size] : null // Use the cart item ID for THIS specific size
        });
        
        console.log(`📦 ${isCartEdit ? 'Updating' : 'Adding'} sku:${size} (${product?.size}) with qty ${isCartEdit ? newQuantity : quantityDifference}, crop:`, sizeCropSettings, `zoom: ${sizeZoom}x, pan:`, sizeCropPosition);
      }
    });

    if (itemsToAdd.length === 0) {
      return; // No changes to save
    }

    // Calculate total quantity difference for display
    const totalQuantityDiff = itemsToAdd.reduce((sum, item) => sum + Math.abs(item.quantity), 0);
    
    // Clear saved crop data from localStorage since we're now persisting to cart/database
    const imageKey = image.filename; // Always use filename as key for consistency
    try {
      const savedCrops = JSON.parse(localStorage.getItem('tempCropData') || '{}');
      if (savedCrops[imageKey]) {
        delete savedCrops[imageKey];
        localStorage.setItem('tempCropData', JSON.stringify(savedCrops));
        console.log(`🧹 Cleared temporary crop data for ${imageKey} - now saved to cart`);
      }
    } catch (error) {
      console.error('Failed to clear temporary crop data:', error);
    }
    
    // Pass all items for batch adding
    onSave({ items: itemsToAdd, totalQuantity: totalQuantityDiff });
    
    // Update grid thumbnails for all sizes being added to cart
    if (onUpdateThumbnail && image) {
      itemsToAdd.forEach(async (item) => {
        try {
          const sizeConfig = printSizes[item.sku];
          const sizeZoom = item.zoom || 1;
          const sizeOrientation = item.aspectOrientation || 'landscape';
          
          // Get the croppedAreaPixels for this size
          let pixelsToUse = null;
          if (item.sku === previewSize && croppedAreaPixels) {
            pixelsToUse = croppedAreaPixels;
          } else if (cropsPerSize[item.sku]?.croppedAreaPixels) {
            pixelsToUse = cropsPerSize[item.sku].croppedAreaPixels;
          }
          
          if (pixelsToUse && currentImageSrc) {
            const thumbnailBase64 = await generateCroppedThumbnail(
              currentImageSrc,
              pixelsToUse,
              sizeZoom,
              sizeOrientation,
              sizeConfig
            );
            
            if (thumbnailBase64) {
              const ratioId = sizeConfig 
                ? `${sizeConfig.crop_ratio_x}:${sizeConfig.crop_ratio_y}`
                : item.sku;
                
              onUpdateThumbnail(image.filename, item.sku, {
                id: `${image.filename}_${ratioId}_${Date.now()}`,
                data: thumbnailBase64,
                ratio: ratioId,
                aspectOrientation: sizeOrientation,
                timestamp: Date.now()
              });
              console.log(`✅ Updated grid thumbnail for ${image.filename} - ${item.sku}`);
            }
          }
        } catch (error) {
          console.error(`Failed to update thumbnail for ${item.sku}:`, error);
        }
      });
    }
  };

  return (
    <div className="modal-fullscreen bg-white dark:bg-gray-900 flex flex-col lg:flex-row">
      {/* Left: Image Preview Area - Dynamic width based on panel state */}
      <div className="flex flex-col transition-all duration-300" style={{ width: isPanelOpen ? '65%' : '100%', height: '100vh' }}>
        {/* Top toolbar with navigation */}
        <div className="flex justify-between items-center p-3 bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <button
              onClick={handleUndo}
              className="reset-btn px-3 py-2 border border-gray-300 dark:border-white text-gray-900 dark:text-white hover:bg-custom-orange hover:text-white rounded transition-colors"
              title={t('tooltip.reset', 'Reset all changes to this image')}
            >
              <i className="pi pi-undo text-lg mr-2"></i>
              <span className="text-sm">Reset</span>
            </button>
            
            <button
              onClick={() => onDeleteImage && onDeleteImage(image.id || image.filename)}
              className="delete-btn px-3 py-2 border border-red-500 text-red-500 hover:bg-red-500 hover:text-white rounded transition-colors"
              title="Delete this image"
            >
              <i className="pi pi-trash text-lg mr-2"></i>
              <span className="text-sm">Delete</span>
            </button>
          </div>
          {/* Orientation & Rotation Controls - Moved to header */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => toggleOrientation('landscape')}
              className={`animated-border-element landscape-btn px-3 py-1.5 rounded transition-colors text-sm ${aspectOrientation === 'landscape' ? 'selected-orange' : 'border border-gray-300 dark:border-gray-600 bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'}`}
              title={t('tooltip.landscape', 'Switch to landscape orientation')}
            >
              <i className="pi pi-image mr-1"></i>
              Landscape
            </button>
            
            <button
              onClick={() => toggleOrientation('portrait')}
              className={`animated-border-element portrait-btn px-3 py-1.5 rounded transition-colors text-sm ${aspectOrientation === 'portrait' ? 'selected-orange' : 'border border-gray-300 dark:border-gray-600 bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white'}`}
              title={t('tooltip.portrait', 'Switch to portrait orientation')}
            >
              <i className="pi pi-mobile mr-1"></i>
              Portrait
            </button>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                handleCloseWithConfirmation(() => {
                  onClose();
                  if (setActiveTab) {
                    setActiveTab('select');
                  }
                });
              }}
              className="animated-border-element px-3 py-2 border border-gray-300 dark:border-white text-gray-900 dark:text-white hover:bg-custom-orange hover:text-white rounded transition-colors"
              title="Back to image grid"
            >
              <i className="pi pi-images text-lg mr-2"></i>
              <span className="text-sm">Back to Images</span>
            </button>
            <button
              onClick={() => {
                handleCloseWithConfirmation(() => {
                  onClose();
                });
              }}
              className="close-btn px-3 py-2 border border-gray-300 dark:border-white text-gray-900 dark:text-white hover:bg-custom-orange hover:text-white rounded transition-colors"
              title="Close editor"
            >
              <i className="pi pi-times text-lg mr-2"></i>
              <span className="text-sm">Close</span>
            </button>
          </div>
        </div>

        {/* Zoom controls */}
        <div className="p-3 bg-gray-100 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <span className="text-gray-900 dark:text-white font-medium whitespace-nowrap">{t('editor.zoom', 'Zoom')}</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1"
              title={t('tooltip.zoom', 'Adjust zoom level')}
            />
            <span className="text-gray-900 dark:text-white text-sm w-12">{zoom.toFixed(1)}x</span>
          </div>
        </div>

        {/* Image crop editor with react-easy-crop */}
        <div className="flex-1 relative" style={{
          backgroundColor: '#808080',
          backgroundImage: `
            linear-gradient(45deg, #666666 25%, transparent 25%),
            linear-gradient(-45deg, #666666 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #666666 75%),
            linear-gradient(-45deg, transparent 75%, #666666 75%)
          `,
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
        }}>
          {isInitialized && currentImageSrc && (
            <Cropper
              key={`${image.filename}-${previewSize}`}
              image={currentImageSrc}
              crop={crop}
              zoom={zoom}
              aspect={getAspectRatioNumber()}
              onCropChange={handleCropChange}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              cropShape="rect"
              showGrid={true}
              style={{
                containerStyle: {
                  background: 'transparent'
                },
                mediaStyle: {},
                cropAreaStyle: {
                  border: '3px solid #fff',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)'
                }
              }}
            />
          )}
          
          {/* Navigation moved to thumbnail strip below */}
        </div>
        
        {/* Print Size Thumbnails - Shows cropped previews for each size */}
        <div className="bg-gray-100 dark:bg-gray-900 p-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 justify-center">
            {/* Navigation hidden for now - needs debugging */}
            
            <p className="text-white text-xs mr-2">Print Sizes:</p>
            {/* Thumbnail strip for each print size */}
            <div className="flex gap-2 overflow-x-auto py-1">
              {Object.entries(printSizes).map(([sizeKey, sizeConfig]) => {
                // Get cropped thumbnail for this size if it exists
                const cropData = cropsPerSize[sizeKey];
                const hasCrop = !!cropData;
                
                // Calculate aspect ratio based on orientation
                const ratioX = sizeConfig.crop_ratio_x;
                const ratioY = sizeConfig.crop_ratio_y;
                
                // For current preview size, use live state; for others use saved data
                const orientation = sizeKey === previewSize 
                  ? aspectOrientation 
                  : (cropData?.aspectOrientation || 'landscape');
                
                // Determine if naturally landscape or portrait
                const isNaturallyLandscape = ratioX > ratioY;
                
                // Calculate base aspect ratio based on orientation
                let basePadding;
                if (orientation === 'portrait') {
                  // Portrait: taller container
                  basePadding = isNaturallyLandscape
                    ? (ratioX / ratioY) * 100  // 6x4 portrait: 6/4 = 150%
                    : (ratioY / ratioX) * 100; // 8x10 portrait: 10/8 = 125%
                } else {
                  // Landscape: wider container  
                  basePadding = isNaturallyLandscape
                    ? (ratioY / ratioX) * 100  // 6x4 landscape: 4/6 = 66.67%
                    : (ratioX / ratioY) * 100; // 8x10 landscape: 8/10 = 80%
                }
                
                const aspectRatioPadding = basePadding;
                
                return (
                  <div
                    key={sizeKey}
                    onClick={() => {
                      console.log(`📐 Switching to size: ${sizeKey}`);
                      setPreviewSize(sizeKey);
                      
                      // Auto-set quantity to 1 if it's 0
                      if (!sizeQuantities[sizeKey] || sizeQuantities[sizeKey] === 0) {
                        setSizeQuantities(prev => ({
                          ...prev,
                          [sizeKey]: 1
                        }));
                      }
                    }}
                    className={`filmstrip-thumbnail animated-border-element flex-shrink-0 cursor-pointer rounded border-2 transition-colors overflow-hidden ${
                      sizeKey === previewSize 
                        ? 'border-orange-400 ring-2 ring-orange-300' 
                        : 'border-gray-600 hover:border-gray-400'
                    }`}
                    style={{ width: '80px' }}
                    title={sizeConfig.name}
                  >
                    {/* Container with aspect ratio */}
                    <div 
                      className="relative bg-gray-800"
                      style={{ 
                        width: '100%',
                        paddingBottom: `${aspectRatioPadding}%`
                      }}
                    >
                      {/* Show thumbnail */}
                      {hasCrop && cropData.croppedThumbnail ? (
                        <img
                          src={`data:image/jpeg;base64,${cropData.croppedThumbnail}`}
                          alt={sizeConfig.name}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <img
                          src={image.thumbnail ? `data:image/jpeg;base64,${image.thumbnail}` : `data:image/jpeg;base64,${image.image_data}`}
                          alt={sizeConfig.name}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      )}
                      
                      {/* Size label - Liquid Glass Effect */}
                      <div className="absolute bottom-0 left-0 right-0 text-white text-xs px-1 py-0.5 text-center truncate" style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        backdropFilter: 'blur(10px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(10px) saturate(180%)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.37)'
                      }}>
                        {sizeKey}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Navigation hidden for now - needs debugging */}
          </div>
        </div>
      </div>

      {/* Settings Panel Toggle Button */}
      <button
        onClick={() => setIsPanelOpen(!isPanelOpen)}
        className="animated-border-element bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white px-3 py-3 rounded-l-lg shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all flex items-center gap-2"
        style={{ 
          position: 'fixed',
          top: '50%',
          transform: 'translateY(-50%)',
          right: isPanelOpen ? 'calc(35% - 2px)' : '-2px',
          zIndex: 50,
          writingMode: isPanelOpen ? 'horizontal-tb' : 'vertical-rl',
          textOrientation: isPanelOpen ? 'mixed' : 'mixed'
        }}
        title={isPanelOpen ? 'Close settings panel' : 'Open settings panel'}
      >
        {isPanelOpen ? (
          <>
            <span className="text-sm font-medium">Enlargement Options</span>
            <i className="pi pi-chevron-right text-lg"></i>
          </>
        ) : (
          <>
            <i className="pi pi-chevron-left text-lg"></i>
            <span className="text-sm font-medium" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>Enlargement Options</span>
          </>
        )}
      </button>

      {/* Right: Settings Slide-out Panel */}
      <div className={`settings-panel-slideout bg-white dark:bg-gray-800 overflow-y-auto p-6 border-l-2 border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out`}
        style={{ 
          position: 'fixed',
          right: isPanelOpen ? '0' : '-100%',
          top: '0',
          width: '35%',
          minWidth: '400px',
          maxWidth: '600px',
          height: '100vh',
          zIndex: 40,
          boxShadow: isPanelOpen ? '-4px 0 12px rgba(0, 0, 0, 0.15)' : 'none'
        }}
      >
        <h3 className="text-lg font-semibold mb-4">Print Settings</h3>
        
        {/* Current preview info */}
        <div className="mb-4 p-3 bg-orange-50 rounded-lg border-2 border-orange-300">
          <p className="text-sm font-medium text-gray-700">{t('editor.current_selection', 'Current Selection')}:</p>
          <p className="text-base font-bold text-custom-orange">{printSizes[previewSize]?.name || previewSize}</p>
          <p className="text-xs text-gray-600 mt-1">
            {t('editor.ratio', 'Ratio')}: {printSizes[previewSize]?.crop_ratio_x || 3}:{printSizes[previewSize]?.crop_ratio_y || 2}
          </p>
          
          {/* COMMENTED OUT - Auto-save is working well without manual button
          Save Crop Button - Using plain button for reliability
          <button
            onClick={(e) => {
              e.stopPropagation();
              console.log('🔘 Save Crop button clicked!');
              console.log('croppedAreaPixels:', croppedAreaPixels);
              console.log('crop:', crop);
              console.log('zoom:', zoom);
              
              // Always save, even if croppedAreaPixels is null (will use current zoom/position)
              setCropsPerSize(prev => ({
                ...prev,
                [previewSize]: {
                  crop,
                  zoom,
                  croppedAreaPixels,
                  aspectOrientation
                }
              }));
              console.log(`💾 Saved crop for size ${previewSize}`);
              alert(`Crop saved for ${printSizes[previewSize]?.name}!`);
            }}
            style={{
              width: '100%',
              marginTop: '8px',
              padding: '8px 16px',
              backgroundColor: '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            ✂️ SAVE CROP FOR THIS SIZE
          </button>
          */}
        </div>

        <Separator className="my-4" />

        {/* Print sizes and quantities */}
        <div>
          <h4 className="font-medium text-gray-900 mb-3">{t('editor.select_sizes', 'Select Sizes & Quantities')}</h4>
          {/* COMMENTED OUT - instruction no longer needed
        <p className="text-xs text-gray-600 mb-2 italic">
          💡 Adjust crop, then click "Save Crop" before switching sizes
        </p>
        */}
        <div className="space-y-2">
            {Object.entries(printSizes).map(([key, value]) => (
              <div 
                key={key} 
                className="animated-border-element flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors cursor-pointer"
                onClick={() => {
                  setPreviewSize(key);
                  // Auto-set quantity to 1 if currently 0
                  if (!sizeQuantities[key] || sizeQuantities[key] === 0) {
                    setSizeQuantities(prev => ({
                      ...prev,
                      [key]: 1
                    }));
                    console.log(`📸 Auto-set quantity to 1 for ${value.name}`);
                  }
                }}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{value.name}</span>
                    {previewSize === key && (
                      <Badge variant="default" className="bg-blue-500">Viewing</Badge>
                    )}
                    {cropsPerSize[key] && (
                      <Badge variant="secondary" className="bg-green-100 text-green-800"><i className="pi pi-crop"></i> {t('editor.cropped', 'Cropped')}</Badge>
                    )}
                  </div>
                  <div className="text-sm">
                    <p className="text-gray-900 font-medium">${(value.price / 100).toFixed(2)} each</p>
                    {value.sku && bulkDiscounts[value.sku] && (
                      <div className="mt-0.5">
                        {(sizeQuantities[key] || 0) >= bulkDiscounts[value.sku].discount_quantity ? (
                          <p className="text-green-600 text-xs font-semibold flex items-center gap-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            ${(bulkDiscounts[value.sku].discount_price / 100).toFixed(2)} each (discount applied!)
                          </p>
                        ) : (
                          <p className="text-orange-600 text-xs">
                            ${(bulkDiscounts[value.sku].discount_price / 100).toFixed(2)} when qty ≥ {bulkDiscounts[value.sku].discount_quantity}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label htmlFor={`qty-${key}`} className="text-sm text-gray-600">{t('editor.quantity', 'Qty')}:</label>
                  <QuantityInput
                    value={sizeQuantities[key] || 0}
                    onChange={(newQty) => {
                      handleQuantityChange(key, newQty);
                      
                      // Auto-save crop when user sets a quantity for the currently viewing size
                      if (key === previewSize && newQty > 0 && croppedAreaPixels) {
                        setCropsPerSize(prev => ({
                          ...prev,
                          [key]: {
                            crop,
                            zoom,
                            croppedAreaPixels,
                            aspectOrientation
                          }
                        }));
                        console.log(`✂️ Auto-saved crop for ${printSizes[key]?.name} (qty set to ${newQty})`);
                      }
                    }}
                    min={0}
                    max={999}
                  />
                  
                  {/* Remove button - only show if quantity > 0 */}
                  {(sizeQuantities[key] || 0) > 0 && (
                    <button
                      onClick={() => {
                        const sizeName = printSizes[key]?.name || key;
                        setSizeQuantities(prev => ({
                          ...prev,
                          [key]: 0
                        }));
                        toast.success(`${sizeName} removed from order`);
                      }}
                      className="px-2 py-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                      title="Remove this size"
                    >
                      <i className="pi pi-trash text-sm"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-700">{t('editor.total_quantity', 'Total Quantity')}: {totalQuantity}</p>
          <p className="text-lg font-bold text-green-600">${(totalPrice / 100).toFixed(2)}</p>
        </div>

        {/* Save button - adds CURRENT image to cart */}
        <button
          onClick={handleSave}
          className="animated-border-element add-cart-btn w-full mt-4 px-4 py-3 border-2 border-custom-orange text-custom-orange hover:bg-custom-orange hover:text-white rounded-md flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={totalQuantity === 0}
          title={t('tooltip.add_to_cart', 'Add this image to cart')}
        >
          <i className="pi pi-shopping-cart text-lg"></i>
          <span className="font-medium">
            {isCartEdit || isUpdatingExisting ? t('cart.edit', 'Update Cart') : t('btn.add_to_cart', 'Add This Image to Cart')}
          </span>
        </button>
        
        {/* Go to Cart button - saves and closes modal */}
        <button
          onClick={() => {
            // Save current image first if there are quantities
            if (totalQuantity > 0) {
              handleSave();
            }
            // Close modal
            onClose();
            // Switch to cart tab
            if (setActiveTab) {
              setActiveTab('cart');
            }
          }}
          className="animated-border-element w-full mt-2 px-4 py-2 border-2 border-custom-orange text-custom-orange hover:bg-custom-orange hover:text-white rounded-md flex items-center justify-center gap-2 transition-colors"
          title={t('tooltip.go_to_cart', 'Go to cart')}
        >
          <i className="pi pi-arrow-right text-lg"></i>
          <span className="font-medium">{t('btn.view_cart', 'Go to Cart')}</span>
        </button>
      </div>
      
      {/* Confirmation Dialog for unsaved changes */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">
              <i className="pi pi-exclamation-triangle text-orange-500 mr-2"></i>
              Unsaved Changes
            </DialogTitle>
            <DialogDescription className="text-base pt-4">
              You have adjusted the crop settings for this image. Would you like to save these changes before closing?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              <strong>Save:</strong> Your crop position, zoom, and orientation will be preserved for later use.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              <strong>Discard:</strong> Your changes will be lost and the image will revert to its previous state.
            </p>
          </div>
          <DialogFooter className="flex gap-3 sm:gap-3">
            <Button
              variant="outline"
              onClick={handleConfirmDiscard}
              className="flex-1"
            >
              <i className="pi pi-times mr-2"></i>
              Discard Changes
            </Button>
            <Button
              onClick={handleConfirmSave}
              className="flex-1 bg-custom-orange hover:bg-orange-600 text-white"
            >
              <i className="pi pi-check mr-2"></i>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
    </div>
  );
}

// Cropped Image Display Component

// Upload Section Component

export default ImageEditorModal;
